import { getJwtPayload } from '@borg/shared-utils'
import { type Auth0ContextInterface } from '@equipmentshare/auth0-react'
import { auth0Cache } from '../auth0Cache'
import { isProduction, goToOutsidePage, LOGIN_ROUTE } from '../utils'
import {
	AUTH0_PRODUCTION_DOMAIN,
	AUTH0_PRODUCTION_CLIENT_ID,
	AUTH0_PRODUCTION_AUDIENCE,
	AUTH_0_LOGOUT_URI,
	AUTH_0_REDIRECT_URI,
	AUTH0_STAGE_DOMAIN,
	AUTH0_STAGE_CLIENT_ID,
	AUTH0_STAGE_AUDIENCE,
} from '../config'

export type Auth0ApplicationConfiguration = {
	audience: string,
	clientId: string,
	domain: string,
	redirectUri: string,
}

export type AuthType = {
	jwt: string | null,
	companyId: number | null,
	userId: number | null,
	securityLevelId: number | null,
}

export const getAuth0ApplicationConfiguration = (): Auth0ApplicationConfiguration => {
	const isProductionEnvironment = isProduction()

	return {
		audience: isProductionEnvironment ? AUTH0_PRODUCTION_AUDIENCE : AUTH0_STAGE_AUDIENCE,
		clientId: isProductionEnvironment ? AUTH0_PRODUCTION_CLIENT_ID : AUTH0_STAGE_CLIENT_ID,
		domain: isProductionEnvironment ? AUTH0_PRODUCTION_DOMAIN : AUTH0_STAGE_DOMAIN,
		redirectUri: AUTH_0_REDIRECT_URI,
	}
}

export const logOut = ( { logout }: Auth0ContextInterface | { logout: Function } = auth0Cache ): void => {
	const { clientId } = getAuth0ApplicationConfiguration()

	logout( { client_id: clientId, returnTo: AUTH_0_LOGOUT_URI } )
}

export const parseJWT = ( token: string ): AuthType => {
	try {
		const parsedToken = getJwtPayload( token )
		const { aud: audience } = parsedToken
		const audienceClaim = Array.isArray( audience ) ? audience[ 0 ] : audience

		if ( !audienceClaim ) {
			throw new Error( 'Invalid JWT: missing audience claim' )
		}

		return {
			jwt: token,
			companyId: parsedToken[ `${audienceClaim}/es_company_id` ] as number | null,
			userId: parsedToken[ `${audienceClaim}/es_user_id` ] as number | null,
			securityLevelId: parsedToken[ `${audienceClaim}/es_security_level_id` ] as number | null,
		}
	} catch ( error: unknown ) {
		throw new Error( String( error ) )
	}
}

export const attemptToGetAuthToken = async( /* istanbul ignore next */ auth0: Auth0ContextInterface | { getAccessTokenSilently: Function } = auth0Cache ): Promise<AuthType> => {
	const { audience }: Auth0ApplicationConfiguration = getAuth0ApplicationConfiguration()

	try {
		const jwt: string = await auth0.getAccessTokenSilently( { audience } )
		return parseJWT( jwt )
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch ( { error }: any ) {
		throw new Error( error )
	}
}

export const getAuthOrRedirect = async(): Promise<AuthType> => {
	try {
		return await attemptToGetAuthToken()
	} catch {
		goToOutsidePage( LOGIN_ROUTE )
		throw new Error( 'Not logged in' )
	}
}
